import { cn, useAnimatedValue } from "@gotts.ai/ui";

import { useBalanceDelta } from "../hooks/useBalanceDelta";
import { formatNumber } from "../lib/format";

interface AnimatedBalanceProps {
  value: number;
  prefix?: string;
  decimals?: number;
  compact?: boolean;
  className?: string;
  /** Custom formatter applied to the animated intermediate value */
  format?: (n: number) => string;
  /** Text appended after the formatted value */
  suffix?: string;
  /** Tooltip showing the full-precision target value */
  fullTitle?: string;
  /** Animation duration in ms (default 500) */
  duration?: number;
  /** Round animated value to nearest integer before formatting */
  integer?: boolean;
  /** Show em-dash when value is zero (default true) */
  emDash?: boolean;
}

/**
 * Displays a numeric balance with smooth count-up/down animation and
 * directional green/red flash on change.
 *
 * Uses `useAnimatedValue` from @gotts.ai/ui for smooth interpolation and
 * `useBalanceDelta` for direction detection + CSS flash classes.
 */
export function AnimatedBalance({
  value,
  prefix = "",
  decimals = 2,
  compact = true,
  className,
  format,
  suffix,
  fullTitle,
  duration = 500,
  integer = false,
  emDash = true,
}: AnimatedBalanceProps) {
  const animated = useAnimatedValue(value, { duration });
  const { direction, isFlashing } = useBalanceDelta(value, duration);

  if (emDash && value === 0) {
    return (
      <span
        className={cn("font-mono text-[var(--color-text-muted)]", className)}
      >
        {"\u2014"}
      </span>
    );
  }

  const displayValue = integer ? Math.round(animated) : animated;

  // When integer mode is on and no custom format, use 0 decimal places
  const effectiveDecimals = integer && !format ? 0 : decimals;

  let displayText: string;
  if (format) {
    displayText = `${prefix}${format(displayValue)}${suffix ?? ""}`;
  } else {
    const formatted = formatNumber(displayValue, {
      prefix,
      decimals: effectiveDecimals,
      compact,
    });
    displayText = `${formatted.display}${suffix ?? ""}`;
  }

  // Title always shows the final target value, not the animated intermediate
  const titleValue =
    fullTitle ??
    (format
      ? `${prefix}${format(value)}${suffix ?? ""}`
      : formatNumber(value, { prefix, decimals: effectiveDecimals, compact })
          .full);

  return (
    <span
      className={cn(
        "font-mono transition-colors",
        isFlashing && direction === "up" && "animate-balance-up",
        isFlashing && direction === "down" && "animate-balance-down",
        className,
      )}
      title={titleValue}
    >
      {displayText}
    </span>
  );
}
